import React, { useEffect, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import ImageUploader from "quill-image-uploader";
// import ImageResize from "quill-image-resize-module-react";
import "react-quill/dist/quill.snow.css";
import "./Program.scss";
import QuillResizeImage from "quill-resize-image";

Quill.register("modules/imageUploader", ImageUploader);
// Quill.register("modules/imageResize", ImageResize);
Quill.register("modules/resizeImage", QuillResizeImage);

const ATTRIBUTES = ["alt", "height", "width", "style"];

const ParchmentEmbed = Quill.import("blots/block/embed");
class ImageWithStyle extends ParchmentEmbed {
  static create(value) {
    let node = super.create(value);
    if (typeof value === "string") {
      node.setAttribute("src", this.sanitize(value));
    }
    return node;
  }

  static formats(domNode) {
    return ATTRIBUTES.reduce(function (formats, attribute) {
      if (domNode.hasAttribute(attribute)) {
        formats[attribute] = domNode.getAttribute(attribute);
      }
      return formats;
    }, {});
  }

  static match(url) {
    return /\.(jpe?g|gif|png)$/.test(url) || /^data:image\/.+;base64/.test(url);
  }

  static sanitize(url) {
    return url;
    //return sanitize(url, ['http', 'https', 'data']) ? url : '//:0';
  }

  static value(domNode) {
    return domNode.getAttribute("src");
  }

  format(name, value) {
    if (ATTRIBUTES.indexOf(name) > -1) {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
    } else {
      super.format(name, value);
    }
  }
}
ImageWithStyle.blotName = "imagewithstyle";
ImageWithStyle.tagName = "IMG";
Quill.register(ImageWithStyle, true);

var Font = Quill.import("formats/font");
Font.whitelist = [
  "Roboto",
  "Calibri",
  "Poppins",
  "Open_Sans",
  "Montserrat",
  "Oswald",
  "Jaro",
  "Parkinsans",
  "Arial",
  "Georgia",
  "Times_New_Roman",
  "Courier_New",
  "Ubuntu",
  "Raleway",
  "Quicksand",
  "Playfair_Display",
  "Lora",
  "Fira_Sans",
  "Roboto_Mono",
  "Lobster",
  "Permanent_Marker",
];
Quill.register(Font, true);


// Sizes
const Size = Quill.import('attributors/style/size');
Size.whitelist = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '40px'];
Quill.register(Size, true);

const RichTextEditor = ({ value, onChange, index, rawHtml = false }) => {
  const reactQuillRef = useRef(null);
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlDraft, setHtmlDraft] = useState("");
  const lastPreviewHtmlRef = useRef("");
  const suppressNextQuillChangeRef = useRef(false);
  /** When true, the next `value` change came from this editor's onChange — do not re-paste HTML or the caret resets and text appears reversed. */
  const valueUpdateFromQuillRef = useRef(false);

  useEffect(() => {
    const addToolTips = () => {
      const toolbar = document.querySelector(".ql-toolbar");

      if (!toolbar) return;

      const buttons = [
        { selector: ".ql-bold", title: "Bold" },
        { selector: ".ql-italic", title: "Italic" },
        { selector: ".ql-underline", title: "Underline" },
        { selector: ".ql-strike", title: "Strikethrough" },
        { selector: ".ql-list[value='ordered']", title: "Ordered List" },
        { selector: ".ql-list[value='bullet']", title: "Bullet List" },
        { selector: ".ql-link", title: "Insert Link" },
        { selector: ".ql-image", title: "Insert Image" },
        { selector: ".ql-video", title: "Insert Video" },
        { selector: ".ql-formula", title: "Insert Formula" },
        { selector: ".ql-clean", title: "Clear Formatting" },
        { selector: ".ql-direction[value='rtl']", title: "Right-to-Left" },
        { selector: ".ql-script[value='sub']", title: "Subscript" },
        { selector: ".ql-script[value='super']", title: "Superscript" },
        { selector: ".ql-indent[value='-1']", title: "Decrease Indent" },
        { selector: ".ql-indent[value='+1']", title: "Increase Indent" },
        { selector: ".ql-align", title: "Text Alignment" },
        { selector: ".ql-color", title: "Text Color" },
        { selector: ".ql-background", title: "Background Color" },
      ];

      buttons.forEach(({ selector, title }) => {
        const el = toolbar.querySelector(selector);
        if (el) el.setAttribute("title", title);
      });

      // Tooltips for dropdowns
      const selects = toolbar.querySelectorAll("select");
      selects.forEach((select) => {
        if (select.classList.contains("ql-font")) {
          select.setAttribute("title", "Font");
        }
        if (select.classList.contains("ql-size")) {
          select.setAttribute("title", "Font Size");
        }
      });
    };

    setTimeout(addToolTips, 500); // slight delay ensures toolbar is rendered
  }, []);

  useEffect(() => {
    const syncListFontSizes = () => {
      const listItems = document.querySelectorAll('.ql-editor li');

      listItems.forEach((li) => {
        const span = li.querySelector('span[style*="font-size"]');
        if (span) {
          const spanSize = window.getComputedStyle(span).fontSize;
          li.style.fontSize = spanSize;
        }
      });
    };

    setTimeout(syncListFontSizes, 100); // Wait for DOM update after list creation
  }, [value]);

  useEffect(() => {
    if (!htmlMode) return;
    // IMPORTANT: never derive draft from Quill, because Quill normalizes HTML
    // and can escape tags into entities. Use the source-of-truth `value`.
    setHtmlDraft(value || "");
  }, [htmlMode, value]);

  useEffect(() => {
    // Render preview in Quill without mutating the saved HTML.
    if (htmlMode) return;
    const editor = reactQuillRef.current?.getEditor?.();
    if (!editor) return;
    const next = value || "";
    if (!next) return;
    if (valueUpdateFromQuillRef.current) {
      valueUpdateFromQuillRef.current = false;
      lastPreviewHtmlRef.current = next;
      return;
    }
    if (lastPreviewHtmlRef.current === next) return;
    lastPreviewHtmlRef.current = next;
    suppressNextQuillChangeRef.current = true;
    editor.clipboard.dangerouslyPasteHTML(next);
    // Quill may emit an onChange after paste; ignore it.
    setTimeout(() => {
      suppressNextQuillChangeRef.current = false;
    }, 0);
  }, [htmlMode, value]);

  const applyHtml = () => {
    const editor = reactQuillRef.current?.getEditor?.();
    // Quill normalizes HTML (drops classes/structure). For Offer Modal we want to
    // preserve the exact HTML user pasted, so we save the raw draft to parent state.
    try {
      onChange(index, htmlDraft || "");
    } catch (e) {
      // ignore
    }
    // Still paste into Quill for a visual preview in rich editor mode.
    if (editor) {
      suppressNextQuillChangeRef.current = true;
      editor.clipboard.dangerouslyPasteHTML(htmlDraft || "");
      setTimeout(() => {
        suppressNextQuillChangeRef.current = false;
      }, 0);
    }
    setHtmlMode(false);
  };





  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button
          type="button"
          onClick={() => setHtmlMode((v) => !v)}
          style={{
            border: "1px solid #d0d0d0",
            background: "#fff",
            padding: "6px 10px",
            borderRadius: 6,
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          {htmlMode ? "Back to editor" : "Edit HTML"}
        </button>
      </div>

      {htmlMode && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 6,
            padding: 10,
            position: "relative",
            zIndex: 2,
            pointerEvents: "auto",
          }}
        >
          <textarea
            value={htmlDraft}
            onChange={(e) => setHtmlDraft(e.target.value)}
            placeholder="<p>Paste HTML here...</p>"
            style={{
              width: "100%",
              minHeight: 260,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              fontSize: 12,
              border: "1px solid #e5e5e5",
              borderRadius: 6,
              padding: 10,
              outline: "none",
              position: "relative",
              zIndex: 1,
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 10,
              position: "relative",
              zIndex: 2,
              pointerEvents: "auto",
            }}
          >
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setHtmlMode(false)}
              style={{
                border: "1px solid #d0d0d0",
                background: "#fff",
                padding: "8px 12px",
                borderRadius: 6,
                fontSize: 12,
                cursor: "pointer",
                pointerEvents: "auto",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={applyHtml}
              style={{
                border: "1px solid #0bd7ee",
                background: "#0bd7ee",
                padding: "8px 12px",
                borderRadius: 6,
                fontSize: 12,
                cursor: "pointer",
                pointerEvents: "auto",
              }}
            >
              Apply HTML
            </button>
          </div>
        </div>
      )}

      {/* Keep Quill mounted even in HTML mode, so we can paste HTML reliably. */}
      <ReactQuill
        ref={reactQuillRef}
        value={value}
        theme="snow"
        readOnly={rawHtml}
        style={{
          minHeight: "25vh",
          display: htmlMode ? "none" : "block",
        }}
        placeholder="Enter Details..."
        onChange={(content) => {
          if (rawHtml) return;
          if (suppressNextQuillChangeRef.current) return;
          valueUpdateFromQuillRef.current = true;
          onChange(index, content);
        }}
        modules={{
          toolbar: [
            [{ font: Font.whitelist }],
            [{ size: Size.whitelist }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            ["link", "image", "video", "formula"],
            [{ align: [] }],
            ["clean"],
            [{ script: "sub" }, { script: "super" }],
            [{ indent: "-1" }, { indent: "+1" }],
            [{ direction: "rtl" }],
          ],
          clipboard: {
            matchVisual: false,
          },
          resizeImage: {
            displaySize: true,
          },
        }}
        formats={[
          // "header",
          "font",
          "size",
          "bold",
          "italic",
          "underline",
          "strike",
          // "blockquote",
          "list",
          "bullet",
          "indent",
          "link",
          "imagewithstyle",
          "image",
          "video",
          "alt",
          "width",
          "height",
          "style",
          "align",
          "color",
          "background",
          "script"
        ]}
      />
    </div>
  );
};

export default React.memo(RichTextEditor);
